// ─────────────────────────── FEATURE → TIER MAP ───────────────────────────
// Single source of truth for the Preview / Member / Pro access ladder
// (Package Structure v2.1). Every gate — client (useTierGate) and server
// (402 enforcement) — resolves through this map, so a feature's tier is declared
// in ONE place, in data, not scattered across the UI.
//
// `Tier` is the ACCESS level. It is distinct from the subscription plan:
//   - Preview  = free, sees everything / accesses nothing
//   - Member   = the $29 operational core (today's free platform)
//   - Pro      = Member + the "information edge" (inventory, briefings, live)
// Team is a plan, not a separate access tier — Team users resolve to `PRO`
// access with a `Team` label (see entitlements.ts).

export type Tier = 'PREVIEW' | 'MEMBER' | 'PRO';

export const TIER_RANK: Record<Tier, number> = {
  PREVIEW: 0,
  MEMBER: 1,
  PRO: 2,
};

export const tierRank = (tier: Tier): number => TIER_RANK[tier];

// Human labels. Team is included because a Team plan presents as "Team" even
// though its access tier is PRO.
export const TIER_LABEL: Record<Tier | 'TEAM', string> = {
  PREVIEW: 'Preview',
  MEMBER: 'Member',
  PRO: 'Pro',
  TEAM: 'Team',
};

export type Feature =
  | 'projectAccess' // project pages + comparisons
  | 'dealRegistration' // register + protect a client / deal
  | 'dealManagement' // add / manage / track deals
  | 'earnings' // earnings dashboard + withdrawals
  | 'coreTraining' // core masterclass training
  | 'basicMarketing' // basic marketing generator
  | 'inventory' // real inventory / explore data (Pro edge)
  | 'newsOffers' // news + offers
  | 'briefings' // developer-vault briefings
  | 'aiChat' // AI ask access at all (Member+; Member is capped, Pro uncapped)
  | 'unlimitedAi' // uncapped AI ask (Member is capped, Pro is unlimited)
  | 'marketingStudio' // advanced marketing studio
  | 'liveTraining' // live sessions with a Dubai property expert
  | 'analytics' // advanced analytics
  | 'referrals' // agent referral program
  | 'exclusiveHold'; // hold a 100% Commission unit (ID-119)

// The minimum access tier each feature requires. Mirrors the gating matrix in
// the Epic B plan.
export const FEATURE_MIN_TIER: Record<Feature, Tier> = {
  projectAccess: 'MEMBER',
  dealRegistration: 'MEMBER',
  dealManagement: 'MEMBER',
  earnings: 'MEMBER',
  coreTraining: 'MEMBER',
  basicMarketing: 'MEMBER',
  inventory: 'PRO',
  newsOffers: 'PRO',
  briefings: 'PRO',
  aiChat: 'MEMBER',
  unlimitedAi: 'PRO',
  marketingStudio: 'PRO',
  liveTraining: 'PRO',
  analytics: 'PRO',
  referrals: 'MEMBER',
  exclusiveHold: 'MEMBER',
};

export const ALL_FEATURES = Object.keys(FEATURE_MIN_TIER) as Feature[];

// Does a user at `tier` reach `feature`?
export const tierCan = (tier: Tier, feature: Feature): boolean => tierRank(tier) >= tierRank(FEATURE_MIN_TIER[feature]);

// Build the full { feature: boolean } capability map for an access tier.
export const canMapFor = (tier: Tier): Record<Feature, boolean> => {
  const out = {} as Record<Feature, boolean>;
  for (const feature of ALL_FEATURES) out[feature] = tierCan(tier, feature);
  return out;
};

// The one gate decision, shared by the client composable and server enforcement.
// While gating is DISABLED everything is allowed (the restructure is dormant);
// once enabled, only features the user's tier reaches. Keeping it pure makes the
// dormant-vs-enforced behaviour trivially testable.
export const tierGateAllows = (can: Record<Feature, boolean>, feature: Feature, gatingEnabled: boolean): boolean =>
  !gatingEnabled || can[feature] === true;
